import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useSelectModal } from '~/hooks';
import { SelectModalParams } from '~/types';
import { useSelectModalStore } from '~/store';
import Icon from '../Icon/Icon';

interface SelectProps {
  type?: 'small' | 'medium';
  width?: string;
  modalTitle?: string;
  selectedOption: string;
  isPlaceholder: string;
  selectOptions: any[];
  click: (option: string) => void;
  children: React.ReactNode | string;
}

export default function Select({
  type = 'medium',
  width = '100%',
  modalTitle,
  selectedOption,
  isPlaceholder,
  selectOptions,
  click,
  children,
}: SelectProps) {
  const { openSelectModal } = useSelectModal();
  const isOpen = useSelectModalStore((state) => state.isOpen);

  const selectModalParams: SelectModalParams = {
    modalTitle,
    selectedOption,
    selectOptions,
    click,
  };

  const isPlaceholderClass = isPlaceholder === children ? 'placeholder' : '';

  return (
    <SelectBox
      width={width}
      type={type}
      onClick={() => openSelectModal(selectModalParams)}
    >
      <Value className={isPlaceholderClass}>{children}</Value>
      <div>
        <Icon
          iconName={isOpen ? 'ArrowUp' : 'ArrowDown'}
          size={20}
          color='caption1'
        />
      </div>
    </SelectBox>
  );
}

export const SelectBox = styled.div<{
  type: 'small' | 'medium';
  width: string;
}>`
  ${({ theme, width, type }) => css`
    ${type === 'medium' &&
    css`
      display: flex;
      position: relative;
      align-items: center;
      justify-content: space-between;
      width: ${width};
      height: 44px;
      padding: 11px 12px;
      border-radius: 8px;
      background: ${theme.backgroundColor.bg_dark_3};
      color: ${theme.content.content_light_1};
      ${theme.fontStyle.text.body16_medium};
      text-align: left;
      cursor: pointer;

      & > span {
        display: block;
        width: calc(100% - 40px);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    `}

    ${type === 'small' &&
    css`
      display: flex;
      position: relative;
      align-items: center;
      justify-content: space-between;
      width: 133px;
      height: 32px;
      padding: 5px 0 5px 12px;
      border: 1px solid ${theme.stroke.stroke_1};
      border-radius: 8px;
      color: ${theme.content.content_light_1};
      text-align: left;
      cursor: pointer;

      & > div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-left: 1px solid ${theme.stroke.stroke_1};
      }

      & > span {
        display: block;
        width: calc(100% - 40px);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        ${theme.fontStyle.text.body14_medium};
      }
    `}
  `}
`;

export const Value = styled.span`
  ${({ theme }) => css`
    &.placeholder {
      color: ${theme.content.content_light_5};
    }
  `}
`;
